// ======================================================
// 🧮 Calculation Utilities (Refactored & Optimized)
// ======================================================

import {
  isOriginationRow,
  calcOriginationDollarValue,
  getOwnRowPercent,
} from "../../../../utils/originationFee";

// ---------- Basic Helpers ----------

const parseCurrency = (val) => {
  const n = Number(String(val ?? "").replace(/[^0-9.-]+/g, ""));
  return Number.isFinite(n) ? n : 0;
};

const toFixed2 = (num) => Number((num ?? 0).toFixed(2));

const safeDivide = (a, b) => (b ? a / b : 0);

// ---------- Individual Calculators ----------

const calcPrepaidMonthTaxes = (salesPrice, rate, monthsIns) =>
  safeDivide(salesPrice * rate * monthsIns, 1200);

const calcMonthInsurance = (salesPrice, rateThou, monthsIns, loanType) => {
  if (loanType === "Cash") return (salesPrice * rateThou) / 1000;
  if (monthsIns > 0) return (salesPrice * rateThou * monthsIns) / 12000;
  return 0;
};

const calcDaysInterestAmount = (amount, adjusted, rate, daysInterest) =>
  safeDivide((adjusted || amount) * rate * daysInterest, 36000);

const calcPrincipalAndInterest = (amount, rate, years) => {
  if (amount <= 0 || rate <= 0 || years <= 0) return 0;
  const r = rate / 1200;
  const n = years * 12;
  const factor = Math.pow(1 + r, n);
  return (amount * r * factor) / (factor - 1);
};

const calcHomeOwnerInsurance = (tax, monthsIns) => safeDivide(tax, monthsIns);
const calcRealEstateTaxes = (tax, monthsTaxes) => safeDivide(tax, monthsTaxes);

// ======================================================
// 🔹 Prepaid Payment Calculation
// ======================================================

export const getDaysInterestAmount = (amount, adjusted, rate, daysInterest) =>
  calcDaysInterestAmount(amount, adjusted, rate, daysInterest);

export const getPrepaidMonthTaxes = (salesPrice, rate, monthsTaxes) =>
  calcPrepaidMonthTaxes(salesPrice, rate, monthsTaxes);

export const getMonthInsurance = (salesPrice, rateThou, monthsIns, loanType) =>
  calcMonthInsurance(salesPrice, rateThou, monthsIns, loanType);

export const getPrepaidPaymentData = (formData = {}, result = {}) => {
  const fd = formData || {};
  const meta = fd.loanInfoMeta || {};
  const prepaids = fd.prepaidsMeta || {};

  const salesPrice = parseCurrency(fd.salesPrice);
  const amount = parseCurrency(result.amount);
  const adjusted = parseCurrency(result.adjusted);
  const rate = parseCurrency(prepaids.rate);
  const rateThou = parseCurrency(prepaids.rateThou);
  const monthsIns = parseCurrency(prepaids.monthsIns);
  const monthsTaxes = parseCurrency(prepaids.monthsTaxes);
  const daysInterest = parseCurrency(prepaids.daysInterest);
  const interestRate = parseCurrency(meta.interestRate1);
  const terms = parseCurrency(meta.termsInYears1);
  const loanType = meta.buyersLoanType;

  // console.log("getPrepaidPaymentData() params:", { salesPrice, amount, adjusted, daysInterest, interestRate, terms, loanType });
  const prepaidMonthTaxes = calcPrepaidMonthTaxes(
    salesPrice,
    rate,
    monthsTaxes,
  );
  const monthInsurance = calcMonthInsurance(
    salesPrice,
    rateThou,
    monthsIns,
    loanType,
  );
  const daysInterestAmount = calcDaysInterestAmount(
    amount,
    adjusted,
    interestRate,
    daysInterest,
  );
  const principalAndInterest = calcPrincipalAndInterest(
    amount,
    interestRate,
    terms,
  );
  const homeOwnerInsuranceRes = calcHomeOwnerInsurance(
    monthInsurance,
    monthsIns,
  );
  const realEstateTaxes = calcRealEstateTaxes(prepaidMonthTaxes, monthsTaxes);

  // console.log("getPrepaidPaymentData() result:", { prepaidMonthTaxes, monthInsurance, daysInterestAmount, principalAndInterest, homeOwnerInsuranceRes, realEstateTaxes });
  return {
    prepaidMonthTaxes: toFixed2(prepaidMonthTaxes),
    monthInsurance: toFixed2(monthInsurance),
    daysInterestAmount: toFixed2(daysInterestAmount),
    principalAndInterest: toFixed2(principalAndInterest),
    homeOwnerInsuranceRes: toFixed2(homeOwnerInsuranceRes),
    realEstateTaxes: toFixed2(realEstateTaxes),
    pAndI2ndTDAmount: "0.00",
  };
};

// ======================================================
// 🔹 Second Trust Deed Calculation
// ======================================================

export const getPSecondTd = (formData = {}, result = {}) => {
  const meta = formData.loanInfoMeta || {};
  const amount = parseCurrency(result.amount2);
  const rate = parseCurrency(meta.interestRate2);
  const years = parseCurrency(meta.termsInYears2);

  if (!amount || !rate || !years) return { pAndI2ndTDAmount: "0.00" };

  const r = rate / 1200;
  const n = years * 12;
  const factor = Math.pow(1 + r, n);
  const val = (amount * r * factor) / (factor - 1);

  return { pAndI2ndTDAmount: toFixed2(val) };
};

// ======================================================
// 🔹 Two Month PMI Calculation
// ======================================================

export const getMonthlyPMIAmount = (formData) => {
  let rateMonthlyPMI = formData?.paymentMeta?.rateMonthlyPMI;
  let totalAmount =
    parseCurrency(formData?.loanInfoMeta?.amount) +
    parseCurrency(formData?.loanInfoMeta?.amount2);
  let twoMonthsPMI = safeDivide(totalAmount * rateMonthlyPMI, 1200);

  return {
    rateMonthlyPMIAmount: toFixed2(twoMonthsPMI),
    twoMonthsPMI: toFixed2(twoMonthsPMI * 2),
  };
};
export const getAnnualTaxforPrepaid = (formData) => {
  if (parseCurrency(formData.loanInfoMeta.annualPropertyTax) > 0) {
    return (
      (parseCurrency(formData.loanInfoMeta.annualPropertyTax) / 12) *
      formData.prepaidsMeta.monthsTaxes
    );
  } else {
    return formData.prepaidsMeta.prepaidMonthTaxes;
  }
};

export const getTwoMonthPMI = (
  formData = {},
  templateformData = {},
  result = {},
) => {
  // console.log("getTwoMonthPMI() called with:", { formData, templateformData, result });
  const conv = templateformData?.dataNational?.conventional_loans;
  if (!conv) return "0.00";

  const loan = formData.loanInfoMeta || {};
  const percent1 = parseCurrency(loan.conventionalPercent);
  const percent2 = parseCurrency(loan.conventionalPercent2);
  const totalPercent = percent1 + percent2;
  const years =
    parseCurrency(loan.termsInYears1) + parseCurrency(loan.termsInYears2);
  const totalAmount =
    parseCurrency(result.amount) + parseCurrency(result.amount2);

  const rates =
    years > 15
      ? conv?.pmi_rates_30_year?.renewal_rates_percent
      : conv?.pmi_rates_15_year?.renewal_rates_percent;

  if (!rates) return "0.00";

  let rateValue = 0;
  if (totalPercent > 95 && totalPercent <= 97) rateValue = rates["95_to_97"];
  else if (totalPercent > 90 && totalPercent <= 95)
    rateValue = rates["90_1_to_95"];
  else if (totalPercent > 85 && totalPercent <= 90)
    rateValue = rates["85_1_to_90"];
  else if (totalPercent > 80 && totalPercent <= 85)
    rateValue = rates["80_1_to_85"];

  // console.log("getTwoMonthPMI() params:", { totalPercent, years, totalAmount, rateValue });
  const twoMonthsPMI = safeDivide(totalAmount * rateValue, 1200);

  return {
    rateMonthlyPMI: toFixed2(rateValue),
    rateMonthlyPMIAmount: toFixed2(twoMonthsPMI),
    twoMonthsPMI: toFixed2(twoMonthsPMI * 2),
  };
};

// ======================================================
// 🔹 Conventional Loan Amount Calculation
// ======================================================

export const getAmountConventional = (formData) => {
  // console.log("getAmountConventional() formData:", formData);
  let amount =
    (parseCurrency(formData.salesPrice) *
      formData.loanInfoMeta.conventionalPercent) /
    100;
  let downPayment = formData.salesPrice;
  let amount2 = 0;
  let ltv1 = formData.loanInfoMeta.conventionalPercent;
  let ltv2 = formData.loanInfoMeta.conventionalPercent2;
  if (ltv2 > 0) {
    amount2 = (parseCurrency(formData.salesPrice) * ltv2) / 100; //Formula applied here to calculate the amount
    downPayment = parseCurrency(downPayment) - parseCurrency(amount2); //calculating down payment
  }

  downPayment = parseCurrency(downPayment) - parseCurrency(amount); //calculating down payment

  if (parseCurrency(formData.loanInfoMeta.requestedDownPayment) > 0) {
    downPayment = parseCurrency(formData.loanInfoMeta.requestedDownPayment);
    amount = parseCurrency(formData.salesPrice) - downPayment;
    if (ltv2 > 0) {
      amount = amount - amount2;
    }
    ltv1 = safeDivide(amount * 100, parseCurrency(formData.salesPrice));
  }

  // console.log("getAmountConventional() formData:", formData);
  return {
    amount: parseFloat(amount).toFixed(2),
    amount2: parseFloat(amount2).toFixed(2),
    downPayment: parseFloat(downPayment).toFixed(2),
    ltv1: parseFloat(ltv1).toFixed(2),
    ltv2: parseFloat(ltv2).toFixed(2),
  };
};

//  Owner Carry calculation
export const getAmountOwnerCarry = (formData) => {
  let amount =
    (parseCurrency(formData.salesPrice) *
      parseCurrency(formData.loanInfoMeta.conventionalPercent)) /
    100;
  let amount2 =
    (parseCurrency(formData.salesPrice) *
      parseCurrency(formData.loanInfoMeta.conventionalPercent2)) /
    100;

  let downPayment = formData.salesPrice - amount - amount2;
  return {
    amount: parseFloat(amount).toFixed(2),
    amount2: parseFloat(amount2).toFixed(2),
    downPayment: parseFloat(downPayment).toFixed(2),
    monthsTaxes: "0",
    taxesAmount: "0.00",
    daysInterestAmount: "0.00",
    twoMonthsPMI: "0.00",
    realEstateTaxes: "0.00",
    pAndI2ndTDAmount: "0.00",
    rateMonthlyPMI: "0.00",
    rateMonthlyPMIAmount: "0.00",
  };
};
// USDA Calculation
export const getAmountUSDA = (formData, templateformData) => {
  let salesPrice = parseCurrency(formData?.salesPrice) || 0;

  let usdaCosts = templateformData?.dataNational?.usda_closing_costs || {};
  let loanFactor = usdaCosts?.loan_amount_factor || {};

  let funding_fee_percent = parseCurrency(loanFactor?.mip_factor_percent) || 0;

  let UsdaMipFinance = salesPrice * funding_fee_percent;

  let adjusted = salesPrice + UsdaMipFinance;

  // Create formatted values
  const UsdaMipFinanceFormatted = UsdaMipFinance.toFixed(2);
  const [wholePart] = UsdaMipFinanceFormatted.split(".");

  // Return result object
  return {
    adjusted: parseFloat(adjusted.toFixed(2)),
    amount: salesPrice,
    downPayment: "0.00",
    UsdaMipFinance: parseFloat(UsdaMipFinanceFormatted),
    UsdaMipFinance1: `${wholePart}.00`,
    UsdaMipFinance2: "0.00",
  };
};

// VA Calculation
export const getAmountVA = (formData, templateformData) => {
  const salesPrice = parseCurrency(formData.salesPrice);

  // Extract VA Closing Costs Settings
  const vaCosts = templateformData?.dataNational?.va_closing_costs || {};
  const mortgageInsurance = vaCosts?.mortgage_insurance || {};

  const finance_mip = !!mortgageInsurance.finance_mip;
  const round_down_mip = !!mortgageInsurance.round_down_mip;
  const funding_fee_percent = parseCurrency(
    mortgageInsurance.funding_fee_percent,
  );

  const request = {
    amount: salesPrice,
    FF: funding_fee_percent,
    VA_RoundDownMIP: round_down_mip ? "Y" : "N",
    isfinanceVAMip: finance_mip,
  };
  // console.log("responsePrepaid request:", request);
  // Calculate Funding Finance
  const responsePrepaid = getVaFundingFinance(request);
  // console.log("responsePrepaid:", responsePrepaid);
  return {
    amount: parseFloat(salesPrice).toFixed(2),
    downPayment: parseFloat(0.0).toFixed(2),
    vaffFinanceRate: parseFloat(funding_fee_percent).toFixed(2),
    adjusted: responsePrepaid.adjusted,
    VaFfFin: responsePrepaid.VaFfFin,
    VaFfFin1: responsePrepaid.VaFfFin1,
    VaFfFin2: responsePrepaid.VaFfFin2,
  };
};

// ------------------------------
// Helper Function: Funding Finance Logic
// ------------------------------
function getVaFundingFinance(request) {
  let amount = parseFloat(request.amount ?? 0);
  const FF = parseFloat(request.FF ?? 0);

  // Base calculation
  let VaFfFin = (amount * FF) / 100; // funding fee amount
  VaFfFin = parseFloat(VaFfFin).toFixed(2);

  // Split integer and decimal parts
  const res = VaFfFin.split(".");
  let VaFfFin1 = "0.00";
  let VaFfFin2 = "0.00";

  // Round logic (if round down)
  if (request.VA_RoundDownMIP === "Y") {
    VaFfFin1 = res[0] + ".00";
    if (res[1]) VaFfFin2 = "0." + res[1];
  } else {
    VaFfFin2 = VaFfFin;
  }

  // Adjusted amount logic
  let adjusted = amount;
  if (request.isfinanceVAMip === true) {
    adjusted = amount + parseFloat(VaFfFin1);
  }

  // Final result
  return {
    adjusted: parseFloat(adjusted).toFixed(2),
    VaFfFin: VaFfFin,
    VaFfFin1: VaFfFin1,
    VaFfFin2: VaFfFin2,
  };
}

// ======================================================
// 🔹 FHA Calculation
// ======================================================

export const getAmountFHA = (formData = {}, templateformData = {}) => {
  try {
    const fha = templateformData?.dataNational?.fha_closing_costs;
    if (!fha) return {};

    const salePrice = parseCurrency(formData.salesPrice);
    const terms = parseCurrency(formData.loanInfoMeta?.termsInYears1);
    const maxLoan = parseCurrency(templateformData?.dataCounty?.maxloanamount);
    const ranges = fha?.loan_amount_factor?.ranges || [];

    // Step 1: Determine LTV
    let LTV = 96.5;
    const under = ranges.find((r) => r.label === "under");
    const to = ranges.find((r) => r.label === "to");
    const over = ranges.find((r) => r.label === "over");
    if (under && salePrice <= parseCurrency(under.sale_price))
      LTV = under.ltv_percent;
    else if (to && salePrice <= parseCurrency(to.sale_price))
      LTV = to.ltv_percent;
    else if (over && salePrice > parseCurrency(over.sale_price))
      LTV = over.ltv_percent;

    // Step 2: MIP settings
    const mipYears = fha?.mortgage_insurance?.years || [];
    let MIP = 1.75,
      roundDownMIP = false;
    if (mipYears.length) {
      const match = mipYears.find((m) => terms <= m.years) || mipYears[0];
      MIP = match?.percent ?? MIP;
      roundDownMIP = match?.round_down_mip;
    }

    // Step 3: Calculate FHA base loan
    let amount = (salePrice * LTV) / 100;
    if (maxLoan && amount > maxLoan) amount = maxLoan;

    let adjusted = amount + (amount * MIP) / 100;
    let downPayment = salePrice - amount;

    if (roundDownMIP) {
      amount = Math.floor(amount);
      adjusted = Math.floor(adjusted);
      downPayment = Math.floor(downPayment);
    }

    // Step 4: MIP finance
    const FhaMipFin = amount * (MIP / 100);
    const FhaMipFinRounded = toFixed2(FhaMipFin);
    const [intPart, decPart = "00"] = FhaMipFinRounded.toString().split(".");
    const FhaMipFin1 = `${intPart}.00`;
    const FhaMipFin2 = `0.${decPart}`;

    // Step 5: MMI rate lookup
    const factors = fha?.mmi_factor_setting || [];
    let rateValue = 0.55;
    factors.forEach((f) => {
      const amt = parseCurrency(amount);
      if (f.up_to_amount && amt <= parseCurrency(f.up_to_amount))
        rateValue = f.factor;
      else if (
        f.exceeding_from_amount &&
        amt >= parseCurrency(f.exceeding_from_amount)
      )
        rateValue = f.factor;
    });

    const interestRate = parseCurrency(formData.loanInfoMeta?.interestRate1);
    const daysInterest = parseCurrency(formData.prepaidsMeta?.daysInterest);
    const daysInterestAmount = calcDaysInterestAmount(
      amount,
      adjusted,
      interestRate,
      daysInterest,
    );
    // console.log("calcDaysInterestAmount ", amount, adjusted, daysInterest, interestRate);

    const adjustedAmount = amount + parseCurrency(FhaMipFin2);
    const monthlyRateMMI = (adjustedAmount * rateValue) / 1200;
    const monthlyRate = toFixed2(monthlyRateMMI);
    const monthPmiVal = toFixed2(monthlyRateMMI * 2);

    const principalAndInterest = calcPrincipalAndInterest(
      adjusted,
      interestRate,
      terms,
    );

    return {
      amount: toFixed2(amount),
      adjusted: toFixed2(adjusted),
      downPayment: toFixed2(downPayment),
      LTV,
      MIP,
      roundDownMIP,
      FhaMipFin: FhaMipFinRounded,
      FhaMipFin1,
      FhaMipFin2,
      monthlyRate,
      monthPmiVal,
      rateValue,
      daysInterestAmount,
      principalAndInterest: toFixed2(principalAndInterest),
    };
  } catch (err) {
    console.error("getAmountFHA failed:", err);
    return {};
  }
};

// ======================================================
// 🔹 Buyer Commission
// ======================================================

export const getBuyerCommission = (formData = {}) => {
  const sp = parseCurrency(formData.salesPrice);
  const rate = parseCurrency(formData.buyerBrokerCompensation);
  return toFixed2((sp * rate) / 100);
};

// ======================================================
// 🔹 Totals Computation
// ======================================================

export function computeTotals(data = {}) {
  const safeNum = (v) => parseCurrency(v) || 0;
  const asArray = (v) =>
    Array.isArray(v) ? v : v && typeof v === "object" ? Object.values(v) : [];

  const {
    titleSettlementMeta: title = {},
    prepaidsMeta: prepaids = {},
    paymentMeta: pay = {},
    loanInfoMeta: loan = {},
  } = data;

  /* ------------------------------
   * Title & Closing Costs
   * ------------------------------ */
  const totalTitleSettlement =
    safeNum(title.ownersTitleAmount) +
    safeNum(title.escrowSettlementAmount) +
    safeNum(title.lendersTitleAmount) +
    safeNum(title.discountAmount);

  const buyerClosingCostsTotal = asArray(data.buyerClosingCostsMeta).reduce(
    (sum, it) => {
      let val = safeNum(it?.value);
      // ── Origination-factor % → dollar cost fallback ──
      if (isOriginationRow(it) && (it?.unit || "").trim() === "%") {
        val = Number(
          calcOriginationDollarValue(getOwnRowPercent(it), data.salesPrice),
        );
      }
      return sum + val;
    },
    0,
  );

  const closingCostsTotal = asArray(data.closingCostsMeta).reduce(
    (sum, it) => sum + safeNum(it?.total),
    0,
  );

  const totalClosingCosts =
    totalTitleSettlement + buyerClosingCostsTotal + closingCostsTotal;

  /* ------------------------------
   * Existing Loans — payoff = balances + accrued per-diem interest since
   * the last payment (each loan's own balance*rate/100, summed, prorated
   * over `days` at a 360-day year). Not just the raw balance sum.
   * ------------------------------ */
  const loanBalance1 = safeNum(loan.loansToBePaidBalance1);
  const loanBalance2 = safeNum(loan.loansToBePaidBalance2);
  const loanBalance3 = safeNum(loan.loansToBePaidBalance3);
  const loanRate1 = safeNum(loan.loansToBePaidRate1);
  const loanRate2 = safeNum(loan.loansToBePaidRate2);
  const loanRate3 = safeNum(loan.loansToBePaidRate3);
  const payoffDays = safeNum(loan.days);

  const existingRateTotal =
    (loanBalance1 * loanRate1) / 100 +
    (loanBalance2 * loanRate2) / 100 +
    (loanBalance3 * loanRate3) / 100;

  const totalExistingBalance = loanBalance1 + loanBalance2 + loanBalance3;

  // Truncated (not rounded) — per-diem interest is conventionally quoted
  // this way rather than rounded up.
  const daysInterest =
    Math.floor(((existingRateTotal / 360) * payoffDays) * 100) / 100;

  const existingTotal = totalExistingBalance + daysInterest;

  /* ------------------------------
   * Prepaids
   * ------------------------------ */
  const basePrepaids =
    safeNum(prepaids.taxesAmount) +
    safeNum(prepaids.insAmount) +
    safeNum(prepaids.daysInterestAmount);

  const extendedPrepaids =
    basePrepaids +
    safeNum(prepaids.twoMonthsPMI) +
    safeNum(prepaids.fhaMipAmount) +
    safeNum(prepaids.vaffFinanceAmount) +
    safeNum(prepaids.mipFinanceAmount) +
    safeNum(prepaids.costsAmount);

  const totalPrepaids =
    loan.buyersLoanType === "FHA 203b" ? basePrepaids : extendedPrepaids;

  /* ------------------------------
   * Investment Calculation
   * ------------------------------ */
  let totalInvestment;

  if (loan.buyersLoanType === "FHA 203b") {
    // totalPrepaids here is deliberately the raw taxes+insurance+daysInterest
    // sum, NOT the extended total with twoMonthsPMI/fhaMipAmount/etc. —
    // the `loan.buyersLoanType === "FHA 203b" ? basePrepaids : extendedPrepaids`
    // ternary above already narrows it to that for this branch.
    const allTotal = totalPrepaids + totalClosingCosts + existingTotal;
    // adjusted (not amount) — mirrors VA below: the base loan amount plus
    // the financed MIP premium (see getAmountFHA's `adjusted = amount +
    // amount*MIP/100`), i.e. the actual amount disbursed.
    const mipPortion = safeNum(loan.adjusted) - allTotal;

    // Which FHA MIP-financed value gets subtracted depends on the sign of
    // mipPortion — fhaMip (FhaMipFin1) when positive, fhaMipAmount
    // (FhaMipFin2 or the full FhaMipFin, depending on fhaMipCheck) when
    // negative. This mirrors the Buyer calculator's FhaMipFin1/FhaMipFin3
    // split, but Refinance has no FhaMipFin3 — fhaMipAmount is its existing,
    // pre-existing equivalent. Left as-is; confirm with the FHA calculation
    // owner before changing which field backs the negative branch.
    totalInvestment =
      mipPortion -
      safeNum(mipPortion > 0 ? prepaids.fhaMip : prepaids.fhaMipAmount);
  } else if (loan.buyersLoanType === "VA") {
    totalInvestment =
      safeNum(loan.adjusted) -
      totalPrepaids -
      totalClosingCosts -
      existingTotal;
  } else {
    totalInvestment =
      safeNum(loan.amount) - totalPrepaids - totalClosingCosts - existingTotal;
  }

  const totalInvestmentText =
    totalInvestment < 0 ? "Cash From Borrower" : "Cash To Borrower";

  /* ------------------------------
   * Monthly Payment
   * ------------------------------ */
  const totalMonthlyPayment =
    safeNum(pay.principalAndInterest) +
    safeNum(pay.realEstateTaxes) +
    safeNum(pay.homeownerInsurance) +
    safeNum(pay.rateMonthlyPMIAmount) +
    safeNum(pay.pAndI2ndTDAmount) +
    safeNum(pay.monthlyExpense1Amount) +
    safeNum(pay.monthlyExpense2Amount);

  return {
    existingTotal,
    existingLoanDaysInterest: daysInterest,
    totalClosingCosts,
    totalPrepaids,
    totalMonthlyPayment,
    totalInvestment,
    totalInvestmentText,
  };
}

// ======================================================
// 🔹 Discount Calculation
// ======================================================

export function calculateDiscount(formData = {}, isDiscountChanged) {
  const meta = formData.titleSettlementMeta || {};
  const discountPercent = parseCurrency(meta.discount);
  const discountAmount = parseCurrency(meta.discountAmount);
  const salesPrice = parseCurrency(formData.salesPrice);

  let computedPercent, computedAmount;

  if (isDiscountChanged) {
    computedPercent = toFixed2(discountPercent);
    computedAmount = toFixed2((salesPrice * discountPercent) / 100);
  } else {
    computedAmount = toFixed2(discountAmount);
    computedPercent = salesPrice
      ? toFixed2((discountAmount / salesPrice) * 100)
      : 0;
  }

  return {
    discountPercent: computedPercent,
    discountAmount: computedAmount,
  };
}

// Get Month Info
export function getMonthInfo(month) {
  const monthNames = [
    "jan",
    "feb",
    "mar",
    "apr",
    "may",
    "jun",
    "jul",
    "aug",
    "sep",
    "oct",
    "nov",
    "dec",
  ];
  const currentMonth = monthNames[month - 1];
  const nextMonth = monthNames[month === 12 ? 0 : month];
  return { currentMonth, nextMonth };
}

// Days until next month
export function calculateDaysInterest(d, m, y) {
  const closingDate = new Date(y, m - 1, d);
  const firstDayNextMonth = new Date(y, m, 1);
  return (firstDayNextMonth - closingDate) / (1000 * 60 * 60 * 24);
}

// Estimated Proration
export function calculateEstimatedProration({
  annualPropertyTax,
  prorations,
  days,
}) {
  const perDayTax = annualPropertyTax / 360;
  const daysLeftInMonth = Math.abs(prorations) + days;
  let result = perDayTax * daysLeftInMonth;

  if (prorations < 0) {
    result = -result;
  }

  return {
    estimatedTaxProrations: Number(result.toFixed(2)),
    perDayTax,
    daysLeftInMonth,
  };
}

// getBuyerEstimatedTax()
export function getBuyerEstimatedTax(formData = {}, templateformData = {}) {
  const estimatedClosingDate = formData?.loanInfoMeta?.estimatedClosingDate;
  const annualPropertyTax = parseFloat(
    formData?.loanInfoMeta?.annualPropertyTax || 0,
  );
  const data1Proration = templateformData?.data1Proration || {};

  if (
    !estimatedClosingDate ||
    !annualPropertyTax ||
    !Object.keys(data1Proration).length
  ) {
    return { estimatedTaxProrations: 0 };
  }

  // Extract date info
  const { month, days } = getDated(estimatedClosingDate);
  const { currentMonth } = getMonthInfo(month);

  const prorations = parseFloat(data1Proration[currentMonth] || 0);

  // Use helper
  const prorationResult = calculateEstimatedProration({
    annualPropertyTax,
    prorations,
    days,
  });

  return {
    ...prorationResult,
  };
}

export function setPrepaidData(formData = {}, dataImpound = {}) {
  const estimatedClosingDate = formData?.loanInfoMeta?.estimatedClosingDate;

  const { dated, month, year } = getDated(estimatedClosingDate);
  const { nextMonth } = getMonthInfo(month);
  const monthsTaxes = parseFloat(dataImpound[nextMonth] || 0);
  const daysInterest = calculateDaysInterest(dated, month, year);

  return {
    monthsTaxes,
    daysInterest,
  };
}

export function resultOfTitleEscrow(dataTitleEscrow) {
  let escrowSettlementAmount = dataTitleEscrow?.data?.escrowFee ?? "0.00";
  let ownersTitleAmount = dataTitleEscrow?.data?.titleIns ?? "0.00";
  let lendersTitleAmount = "0.00";

  return {
    ownersTitleAmount: ownersTitleAmount ?? "0.00",
    escrowSettlementAmount: escrowSettlementAmount ?? "0.00",
    lendersTitleAmount: lendersTitleAmount ?? "0.00",
  };
}

export const computeClosingCostTotals = (formData = {}) => {
  const closing = Array.isArray(formData?.closingCostsMeta)
    ? formData?.closingCostsMeta
    : [];

  if (closing.length === 0) {
    return {
      ...formData,
      closingCostsMeta: [],
    };
  }

  const loanType = formData?.loanInfoMeta?.buyersLoanType || "Conventional";
  const sp = parseCurrency(formData?.salesPrice);
  const loan = parseCurrency(formData?.loanInfoMeta?.amount);

  const updated = closing.map((cc) => {
    const rate = parseCurrency(cc.value);
    let total = "0.00";

    if (/%\s*sale/i.test(cc.type)) {
      total = (sp * rate) / 100;
    } else if (/%\s*loan/i.test(cc.type)) {
      total = (loan * rate) / 100;
    } else {
      total = rate;
    }

    // Cash override
    if (loanType === "Cash" && cc.apply === "Y") {
      total = "0.00";
    }

    return {
      ...cc,
      total,
    };
  });

  return {
    ...formData,
    closingCostsMeta: updated,
  };
};

function getDated(dateStr) {
  const d = new Date(dateStr);
  if (isNaN(d)) return { dated: 0, month: 0, year: 0, days: 0 };

  const dated = d.getDate();
  const month = d.getMonth() + 1;
  const year = d.getFullYear();

  let adjustedDate = dated;
  if (month === 2 && dated >= 28) adjustedDate = 30;
  if (dated === 31) adjustedDate = 30;

  return { dated: adjustedDate, month, year, days: dated };
}
